import type { AdvancedSettings, ObjectTypeMapping } from '../../types';

export interface PipeexUiWizardMappingSnapshot {
  centerOfCoordinates: AdvancedSettings['centerOfCoordinates'];
  objectTypeNames?: string[];
  customAttributes?: PipeexUiCustomMappingAttribute[];
}

export interface PipeexUiCustomMappingAttribute {
  objectTypeName: string;
  pcfAttributeName: string;
}

export interface PipeexUiWizardMappingSource {
  advanced: AdvancedSettings;
  objectTypes: ReadonlyArray<Pick<ObjectTypeMapping, 'name' | 'attributes'>>;
}

const CanonicalObjectTypeNames = [
  'ALL',
  'BOLT',
  'GASKET',
  'MESSAGE',
  'VALVE',
  'SUPPORT',
  'INSTRUMENT',
  'END-CONNECTION-EQUIPMENT',
  'PIPE',
] as const;

export const normalizeCenterOfCoordinates = (
  value: unknown,
  fallback: AdvancedSettings['centerOfCoordinates'] = 'ProjectBasePoint'
): AdvancedSettings['centerOfCoordinates'] =>
  value === 'SurveyPoint' || value === 'ProjectBasePoint' ? value : fallback;

export function normalizeProfileObjectTypeNames(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }

  const names = new Map<string, string>();
  value.forEach((candidate) => {
    if (typeof candidate !== 'string') {
      return;
    }

    const name = candidate.trim();
    if (name) {
      names.set(name.toUpperCase(), name);
    }
  });

  return Array.from(names.values());
}

export function buildProfileObjectTypeNames(
  objectTypes: ReadonlyArray<Pick<ObjectTypeMapping, 'name'>>
): string[] {
  return normalizeProfileObjectTypeNames(objectTypes.map(({ name }) => name)) ?? [];
}

function normalizeCustomAttributes(
  value: unknown
): PipeexUiCustomMappingAttribute[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }

  const attributes = new Map<string, PipeexUiCustomMappingAttribute>();
  value.forEach((candidate) => {
    if (!candidate || typeof candidate !== 'object') {
      return;
    }

    const { objectTypeName, pcfAttributeName } = candidate as Record<string, unknown>;
    if (typeof objectTypeName !== 'string' || typeof pcfAttributeName !== 'string') {
      return;
    }

    const normalizedObjectTypeName = objectTypeName.trim();
    const normalizedAttributeName = pcfAttributeName.trim();
    if (!normalizedObjectTypeName || !normalizedAttributeName) {
      return;
    }

    attributes.set(
      `${normalizedObjectTypeName.toUpperCase()}\u0000${normalizedAttributeName.toUpperCase()}`,
      {
        objectTypeName: normalizedObjectTypeName,
        pcfAttributeName: normalizedAttributeName,
      }
    );
  });

  return Array.from(attributes.values());
}

export function buildPipeexUiWizardMappingSnapshot(
  source: PipeexUiWizardMappingSource
): PipeexUiWizardMappingSnapshot {
  return {
    centerOfCoordinates: normalizeCenterOfCoordinates(source.advanced.centerOfCoordinates),
    objectTypeNames: buildProfileObjectTypeNames(source.objectTypes),
    customAttributes: normalizeCustomAttributes(
      source.objectTypes.flatMap(({ name, attributes }) =>
        attributes
          .filter(({ isCustom }) => isCustom === true)
          .map(({ pcfAttributeName }) => ({ objectTypeName: name, pcfAttributeName }))
      )
    ) ?? [],
  };
}

export function normalizePipeexUiWizardMappingSnapshot(
  raw: Record<string, unknown>
): PipeexUiWizardMappingSnapshot {
  const objectTypeNames = normalizeProfileObjectTypeNames(raw.objectTypeNames);
  const customAttributes = normalizeCustomAttributes(raw.customAttributes);

  return {
    centerOfCoordinates: normalizeCenterOfCoordinates(raw.centerOfCoordinates),
    ...(objectTypeNames !== undefined ? { objectTypeNames } : {}),
    ...(customAttributes !== undefined ? { customAttributes } : {}),
  };
}

export function restoreProfileAttributeEditability(
  objectTypeMap: Map<string, ObjectTypeMapping>,
  snapshot: PipeexUiWizardMappingSnapshot | undefined
): Map<string, ObjectTypeMapping> {
  const explicitCustomAttributes = snapshot?.customAttributes;
  const customAttributeKeys = new Set(
    explicitCustomAttributes?.map(({ objectTypeName, pcfAttributeName }) =>
      `${objectTypeName.toUpperCase()}\u0000${pcfAttributeName.toUpperCase()}`
    ) ?? []
  );
  const restoreLegacyCustomObjectTypes =
    explicitCustomAttributes === undefined && snapshot?.objectTypeNames !== undefined;
  const canonicalObjectTypes = new Set<string>(CanonicalObjectTypeNames);

  return new Map(
    Array.from(objectTypeMap.entries()).map(([key, objectType]) => {
      const isLegacyCustomObjectType =
        restoreLegacyCustomObjectTypes &&
        !canonicalObjectTypes.has(objectType.name.trim().toUpperCase());
      const attributes = objectType.attributes.map((attribute) => {
        const attributeKey =
          `${objectType.name.trim().toUpperCase()}\u0000${attribute.pcfAttributeName.trim().toUpperCase()}`;
        return isLegacyCustomObjectType || customAttributeKeys.has(attributeKey)
          ? { ...attribute, isCustom: true }
          : attribute;
      });

      return [key, { ...objectType, attributes }] as const;
    })
  );
}

export function getProfileObjectTypeSeeds({
  isFullWizardProfile,
  persistedObjectTypeNames,
  existingObjectTypes,
}: {
  isFullWizardProfile: boolean;
  persistedObjectTypeNames: unknown;
  existingObjectTypes: ObjectTypeMapping[];
}): ObjectTypeMapping[] {
  if (!isFullWizardProfile) {
    return existingObjectTypes;
  }

  const names = normalizeProfileObjectTypeNames(persistedObjectTypeNames)
    ?? [...CanonicalObjectTypeNames];
  return names.map((name) => ({ name, attributes: [] }));
}
